import { Col, Row } from "antd";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { INITIAL_PAGE } from "../../../../utils/helpers/constants";
import FieldBuilder from "../../../form-components/field-builder";
import Form from "../../../form-components/from";
import BinContext from "../../../../context/wms/bin/context";
import { BinPosition, AisleLayout, PlacementSide } from "../../../../models/wms/enums";
import useHierarchicalDropdown from "../../../../hooks/wms/use-hierarchical-dropdown";
import FilterCascadeReset from "../../shared/filter-cascade-reset";

const BinFilter: React.FC = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(BinContext);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    aisles,
    loadingAisles,
    storageUnits,
    loadingStorageUnits,
    shelves,
    loadingShelves,
    visibleLevels,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedAisleId,
    selectedAisleLayout,
    selectedStorageUnitId,
    selectedShelfId,
    selectedSidesFilter,
    selectedSideFilter,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedAisleId,
    setSelectedStorageUnitId,
    setSelectedShelfId,
    setSelectedSidesFilter,
    setSelectedSideFilter,
  } = useHierarchicalDropdown({ debounceMs: 300, shelvesWithBinsOnly: true });

  // Derive valid PlacementSide options from the selected aisle's layout
  const sideOptions = useMemo(() => {
    const allOptions = [
      { label: t("right_side"), value: PlacementSide.R },
      { label: t("left_side"), value: PlacementSide.L },
    ];
    // If a specific aisle is selected, use its layout to filter side options
    const layoutToUse = selectedAisleLayout;

    if (!layoutToUse) return allOptions;
    if (layoutToUse === AisleLayout.LEFT) return [{ label: t("left_side"), value: PlacementSide.L }];
    if (layoutToUse === AisleLayout.RIGHT) return [{ label: t("right_side"), value: PlacementSide.R }];
    return allOptions; // BOTH
  }, [selectedAisleLayout, t]);

  return (
    <Form
      formId="bin-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: query?.keyword,
          filters: [
            selectedStoreId
              ? { name: "storeId", value: selectedStoreId, operation: "eq" }
              : undefined,
            selectedZoneId
              ? { name: "zoneId", value: selectedZoneId, operation: "eq" }
              : undefined,
            selectedAisleId
              ? { name: "aisleId", value: selectedAisleId, operation: "eq" }
              : undefined,
            selectedStorageUnitId
              ? {
                  name: "storageUnitId",
                  value: selectedStorageUnitId,
                  operation: "eq",
                }
              : undefined,
            selectedShelfId
              ? { name: "shelfId", value: selectedShelfId, operation: "eq" }
              : undefined,
            data?.binPosition
              ? {
                  name: "binPosition",
                  value: data.binPosition,
                  operation: "eq",
                }
              : undefined,
            data?.isActive !== undefined && data?.isActive !== null
              ? { name: "isActive", value: data.isActive, operation: "eq" }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <FilterCascadeReset
        rules={[
          {
            watch: selectedStoreId,
            clear: ["zoneId", "aisleId", "storageUnitId", "shelfId", "side", "sides", "zoneTypeId"],
            resetSetters: [setSelectedZoneId, setSelectedAisleId, setSelectedStorageUnitId, setSelectedShelfId, setSelectedSideFilter, setSelectedSidesFilter, setSelectedZoneTypeId],
          },
          {
            watch: selectedZoneId,
            clear: ["aisleId", "storageUnitId", "shelfId", "side", "sides"],
            resetSetters: [setSelectedAisleId, setSelectedStorageUnitId, setSelectedShelfId, setSelectedSideFilter, setSelectedSidesFilter],
          },
          {
            watch: selectedAisleId,
            clear: ["storageUnitId", "shelfId", "side"],
            resetSetters: [setSelectedStorageUnitId, setSelectedShelfId, setSelectedSideFilter],
          },
          {
            watch: selectedStorageUnitId,
            clear: ["shelfId"],
            resetSetters: [setSelectedShelfId],
          },
        ]}
      />
      <Row justify={"start"} gutter={[10, 0]}>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="storeId"
            label={`${t("select_store")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              loading: loadingStores,
              allowSearch: true,
              options: stores.map((s) => ({
                label: s.label,
                value: s.id,
              })),
              onChange: (val) => setSelectedStoreId(val),
            }}
          />
        </Col>
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneTypeId"
              label={t("zone_type")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZoneTypes,
                allowSearch: true,
                options: zoneTypes.map((zt) => ({
                  label: zt.label,
                  value: zt.id,
                })),
                onChange: setSelectedZoneTypeId,
              }}
            />
          </Col>
        )}
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneId"
              label={`${t("select_zone")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZones,
                allowSearch: true,
                options: zones.map((z) => ({
                  label: z.label,
                  value: z.id,
                })),
                onChange: (val) => setSelectedZoneId(val),
              }}
            />
          </Col>
        )}

        {visibleLevels.aisle && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="sides"
              label={t("available_sides")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                options: [
                  { label: t("left_side"), value: AisleLayout.LEFT },
                  { label: t("right_side"), value: AisleLayout.RIGHT },
                  { label: t("both_sides"), value: AisleLayout.BOTH },
                ],
                onChange: setSelectedSidesFilter,
              }}
            />
          </Col>
        )}

        {visibleLevels.aisle && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="aisleId"
              label={`${t("select_aisle")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingAisles,
                allowSearch: true,
                options: aisles.map((a) => ({
                  label: a.label,
                  value: a.id,
                })),
                onChange: (val) => setSelectedAisleId(val),
              }}
            />
          </Col>
        )}

        {visibleLevels.storageUnit && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="side"
              label={t("placement_side")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                options: sideOptions,
                onChange: setSelectedSideFilter,
              }}
            />
          </Col>
        )}

        {visibleLevels.storageUnit && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="storageUnitId"
              label={`${t("select_storage_unit")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingStorageUnits,
                allowSearch: true,
                options: storageUnits.map((su) => ({
                  label: su.label,
                  value: su.id,
                })),
                onChange: (val) => setSelectedStorageUnitId(val),
              }}
            />
          </Col>
        )}
        {visibleLevels.shelf && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="shelfId"
              label={`${t("select_shelf")}`}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingShelves,
                allowSearch: true,
                options: shelves.map((s) => ({
                  label: s.label,
                  value: s.id,
                })),
                onChange: setSelectedShelfId,
              }}
            />
          </Col>
        )}
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="binPosition"
            label={`${t("bin_position")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("position_left"), value: BinPosition.LEFT },
                { label: t("position_center"), value: BinPosition.CENTER },
                { label: t("position_right"), value: BinPosition.RIGHT },
              ],
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="isActive"
            label={`${t("is_active")}`}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("active"), value: 1 },
                { label: t("inactive"), value: 0 },
              ],
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default BinFilter;
